/**
 * Voice rendering helpers.
 *
 * Everything Rally returns carries a `speech` string written for a speaker,
 * not a screen: no markdown, no URLs, no emoji, money said the way people say
 * it, and lists capped so a listener can hold them in memory.
 */

const ONES = [
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

/** 0..999_999 as words ("one hundred twenty-four"). */
export function numberToWords(n: number): string {
  if (!Number.isInteger(n) || n < 0) throw new RangeError(`numberToWords expects a non-negative integer, got ${n}`);
  if (n < 20) return ONES[n]!;
  if (n < 100) {
    const t = TENS[Math.floor(n / 10)]!;
    return n % 10 ? `${t}-${ONES[n % 10]}` : t;
  }
  if (n < 1000) {
    const rest = n % 100;
    return `${ONES[Math.floor(n / 100)]} hundred${rest ? ` ${numberToWords(rest)}` : ''}`;
  }
  if (n < 1_000_000) {
    const rest = n % 1000;
    return `${numberToWords(Math.floor(n / 1000))} thousand${rest ? ` ${numberToWords(rest)}` : ''}`;
  }
  return String(n);
}

/** Display format: 1250 -> "$12.50". */
export function formatMoney(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100).toLocaleString('en-US')}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * Spoken format, the way a cashier says it:
 * 1250 -> "twelve fifty", 1200 -> "twelve dollars", 1205 -> "twelve oh five",
 * 99 -> "ninety-nine cents", 100_000 -> "one thousand dollars".
 */
export function speakMoney(cents: number): string {
  if (cents < 0) return `minus ${speakMoney(-cents)}`;
  const dollars = Math.floor(cents / 100);
  const rest = cents % 100;
  if (dollars === 0) return `${numberToWords(rest)} cent${rest === 1 ? '' : 's'}`;
  if (rest === 0) return `${numberToWords(dollars)} dollar${dollars === 1 ? '' : 's'}`;
  if (dollars >= 1000) return `${numberToWords(dollars)} dollars and ${numberToWords(rest)} cents`;
  const tail = rest < 10 ? `oh ${numberToWords(rest)}` : numberToWords(rest);
  return `${numberToWords(dollars)} ${tail}`;
}

/** "a, b and c" — capped at `max` with "and N more". */
export function speakList(items: string[], max = 3): string {
  const shown = items.slice(0, max);
  const extra = items.length - shown.length;
  const parts = extra > 0 ? [...shown, `${extra} more`] : shown;
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Quantity + noun: (2, "taco") -> "two tacos". */
export function speakCount(n: number, singular: string, plural = `${singular}s`): string {
  return `${n <= 20 ? numberToWords(n) : n} ${n === 1 ? singular : plural}`;
}

/** Local time for speech: "7:05 PM" -> "7:05 PM", "19:00" -> "7 PM". */
export function speakTime(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  const period = parts.find((p) => p.type === 'dayPeriod')?.value ?? '';
  return minute === '00' ? `${hour} ${period}` : `${hour}:${minute} ${period}`;
}

const LINK = /\[([^\]]*)\]\([^)]*\)/g;
const MARKDOWN = /[*_`#>|~]/g;
const URL = /\bhttps?:\/\/\S+/gi;
// Extended pictographic covers emoji; the variation selector and ZWJ glue them together.
const EMOJI = /[\p{Extended_Pictographic}️‍]/gu;

/** Strip things a speaker cannot say well: markdown, links, emoji, doubled whitespace. */
export function sanitizeForSpeech(text: string): string {
  return text
    .replace(LINK, '$1')
    .replace(URL, '')
    .replace(MARKDOWN, '')
    .replace(EMOJI, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .trim();
}

/** Rough speaking time at ~2.6 words/second, used by the latency and verbosity budgets. */
export function estimateSpeechSeconds(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.round((words / 2.6) * 10) / 10;
}
